import type { ProjectElementNode } from '@deltek/specification-client/core/Models/productListingDetail'
import type { UniformatClassificationProperty } from '@deltek/specification-client/core/Models/uniformatClassificationProperty'
import Typography from '@material-ui/core/Typography'
import { Tree } from 'antd'
import { PROPERTY_EDITOR_EDITABLE_TEXT_VARIANT as NODE_TYPES } from 'pages/ProjectsPage/WorkspacePage/components/constants'
import { getAssemblyLevel } from 'pages/ProjectsPage/WorkspacePage/components/ElementsPanel/ElementsPanel'
import React, { Key, memo, useCallback, useMemo } from 'react'
import { NodeContentRenderer, SwitcherIcon } from '.'
import { TransformedNode } from '../hooks/useTreeDataTransformer'
import { useTreeViewStyles } from '../styles'
import type { DataNode } from '../types/types'
import { useExpandedKeys, useWorkspace2Actions } from '../Workspace2Store'

/** The props for the buildTreeNodes function */
interface BuildTreeNodesProps {
    /** The nodes to build the tree from */
    nodes: DataNode[]
    /** The keys of nodes that are expanded */
    expandedKeys: Key[]
    /** The CSS classes used for styling the TreeView */
    treeViewClasses: any
}

/**
 * Recursively builds a tree data structure from an array of DataNodes.
 * Also sets 'data-testid' and 'data-key' attributes for testing and debugging.
 * @param nodes - Array of DataNodes to build the tree from.
 * @param expandedKeys - Array of keys of nodes that are expanded.
 * @param treeViewClasses - Object containing CSS classes used for styling the TreeView.
 * @returns Transformed tree data structure.
 */
const buildTreeNodes = ({
    nodes = [],
    expandedKeys = [],
    treeViewClasses,
}: BuildTreeNodesProps): TransformedNode[] => {
    return nodes.map((node) => ({
        ...node,
        'data-testid': 'tree-child',
        'data-key': node.key,
        className:
            node.type === NODE_TYPES.Article
                ? treeViewClasses.articleContainer
                : '',
        switcherIcon: (
            <SwitcherIcon
                type={node.type}
                isLoading={false}
                isExpanded={expandedKeys?.includes(node.key)}
                node={node}
            />
        ),
        title: <NodeContentRenderer node={node} />,
        children: buildTreeNodes({
            nodes: node.children || [],
            expandedKeys,
            treeViewClasses,
        }),
    }))
}

/**
 * Props for the ReviewAssembly component.
 * @param {Partial<ProjectElementNode> | null} selectedProjectElementNode - The assembly text node to render.
 * @param {Partial<UniformatClassificationProperty>[] | undefined} uniformatClassificationProperties - The list of uniformat classification
 * properties associated with the assembly text.
 * @param {any} classes - The CSS classes to apply to the component.
 */
interface ReviewAssemblyProps {
    selectedProjectElementNode?: Partial<ProjectElementNode> | null
    uniformatClassificationProperties?:
        | Partial<UniformatClassificationProperty>[]
        | undefined
    classes: any
}

/**
 * Component for rendering a list of uniformat classification properties for a
 * given assembly text.
 * @param {ReviewAssemblyProps} ReviewAssemblyProps - The component props.
 * @returns {ReactElement} The rendered ReviewAssembly component.
 */
export const ReviewAssembly = memo<ReviewAssemblyProps>(
    ({
        selectedProjectElementNode,
        uniformatClassificationProperties,
        classes,
    }) => {
        const expandedKeys = useExpandedKeys()
        const {
            setExpandedKeys,
            setClassificationNodes,
        } = useWorkspace2Actions()
        const treeViewClasses = useTreeViewStyles()
        const combinedClasses = useMemo(
            () => ({ ...classes, ...treeViewClasses }),
            [classes, treeViewClasses]
        )
        const assemblyText = selectedProjectElementNode?.text ?? ''
        // Gets the assembly level from the assembly text.
        const assemblyLevel = useMemo(
            () => getAssemblyLevel(assemblyText.split('|')[0]),
            [assemblyText]
        )
        /**
         * Checks if the assembly level is a division level.
         * @returns True if the assembly level is a division level, false otherwise.
         */
        const isDivisionLevel = useMemo(
            () => assemblyText.split('|')[0]?.length === 2,
            [assemblyText]
        )
        // Builds the classification nodes from the uniformat classification properties.
        const classificationNodes: DataNode[] = useMemo(() => {
            const nodes = uniformatClassificationProperties?.map((element) => ({
                key: element.id ?? '',
                type: NODE_TYPES.Article,
                order: 0,
                contentItemNodes: [
                    {
                        id: element.id ?? '',
                        text: element.name ?? '',
                        type: NODE_TYPES.Article,
                        order: 0,
                    },
                ],
                children: [
                    {
                        key: element.hint ?? '',
                        type: NODE_TYPES.AssemblyText,
                        order: 0,
                        contentItemNodes: [
                            {
                                id: element.firmId ?? '',
                                text: element.hint ?? '',
                                type: NODE_TYPES.AssemblyText,
                                order: 0,
                            },
                        ],
                    },
                ],
            }))

            if (nodes) {
                setExpandedKeys(nodes.map((node) => node.key))
                setClassificationNodes(nodes)
                return nodes
            }

            return []
        }, [uniformatClassificationProperties])
        // Builds the tree nodes from the classification nodes.
        const treeData = useMemo(
            () =>
                buildTreeNodes({
                    nodes: classificationNodes,
                    expandedKeys,
                    treeViewClasses: combinedClasses,
                }),
            [classificationNodes, expandedKeys, combinedClasses]
        )
        const handleExpand = useCallback((newExpandedKeys: Key[]): void => {
            setExpandedKeys(newExpandedKeys)
        }, [])

        return (
            <div
                style={{ height: '100%', paddingTop: '1.6rem' }}
                data-testid="review-assembly"
            >
                {([2, 3].includes(assemblyLevel) || isDivisionLevel) && (
                    <div className={combinedClasses.assemblyLabelContainer}>
                        <Typography
                            component="span"
                            className={combinedClasses.grayText}
                            display="block"
                        >
                            {isDivisionLevel
                                ? 'No properties associated found'
                                : 'Select the Parent of this Subassembly in order to view Performance Requirements. Assembly parents are designated with 5 characters (ex.A1010)'}
                        </Typography>
                    </div>
                )}

                {assemblyLevel === 1 && !isDivisionLevel && (
                    <div className={combinedClasses.workspaceEditorContainer}>
                        <div
                            className={`${combinedClasses.treeContainer} ${
                                classes || ''
                            }`}
                        >
                            <Tree
                                data-testid="ant-tree"
                                treeData={treeData}
                                onExpand={handleExpand}
                                expandedKeys={expandedKeys}
                                className={combinedClasses.tree}
                            />
                        </div>
                    </div>
                )}
            </div>
        )
    }
)
